// @flow

import {Tag, isMarkdocAstTag} from 'docs/util';
import type {
  MarkdocTagRegistration,
  MarkdocFragmentRegistration,
} from 'docs/markdoc';
import type {RenderNode} from '@markdoc/markdoc';
import type {
  CodeGenerator,
  CodeGeneratorSnippetArgs,
  Lang,
} from '../../../../../server/services';
import {codegenNodeToCodegenArgs, BETA_LANGUAGES, LANGUAGES} from './constants';

type OverridenCodeBlocks = {
  [lang: Lang]: Array<RenderNode>,
};

export const CodegenSnippet: MarkdocTagRegistration = {
  tag: 'codegen-snippet',
  component: 'CodegenSnippet',
  children: ['codegen-snippet-override'],
  attributes: {
    path: {
      type: String,
      required: true,
    },
    method: {
      type: String,
      required: true,
      matches: ['GET', 'POST', 'DELETE'],
    },
    args: {
      type: Object,
      required: true,
    },
    betaHeader: {
      type: String,
      required: false,
      description: 'The beta name passed into the Stripe-Version header',
    },
    gated: {
      type: Array,
      required: false,
      description:
        'An array of values that specify which of the args are gated',
    },
    highlight: {
      type: Array,
      required: false,
      description: '(Alpha!) Attempt to highlight certain args',
    },
  },
  validate(node, config) {
    // This validation is complicated. There is a prerender script that is run in a separate bazel
    // task which calls printExampleRequest for each codegen-snippet and saves that into a cache.

    // The pros of that are having a separate bazel task makes it more efficient (and is required, since
    // we need to output content to a cache).

    // The tradeoff is we are not able to validate content via the vs code extension.

    // TODO (DEVSDK-463): Add fine grained Markdoc validation that works in the vs code extension.

    return [];
  },
  render(node, config) {
    // We do code gen server side, so we do not have to ship the
    // api codegen library bundle to the client.

    const children = node.renderChildren(config);
    const overriddenCodeBlocks: OverridenCodeBlocks = children.reduce(
      (map, child) => {
        // Since we called node.renderChildren, child will be a
        // Ast.Tag value that will correspond to to a React CodeBlock
        // element.
        if (!isMarkdocAstTag(child)) {
          return map;
        }
        let lang = (child.attributes: any)?.language;

        if (lang === 'bash') {
          lang = 'cli';
        }

        map[lang] = child.children;
        return map;
      },
      {},
    );

    const codeGenerator: CodeGenerator = config.services.codeGenerator;
    const requestArgs: CodeGeneratorSnippetArgs = codegenNodeToCodegenArgs(
      node,
      config,
    );

    const codegenStartTime = process.hrtime();

    const langsToGenerate = node.attributes.betaHeader
      ? BETA_LANGUAGES
      : LANGUAGES;

    const generatedCodeBlocks = langsToGenerate
      .map((lang) => {
        let overriddenCodeBlock = null;
        let generatedCode = null;
        let codegenError = null;

        if (overriddenCodeBlocks[lang]) {
          overriddenCodeBlock = overriddenCodeBlocks[lang];
        } else {
          try {
            generatedCode = codeGenerator.printExampleRequest(
              lang,
              requestArgs,
            );
          } catch (e) {
            generatedCode = null;
            /* eslint-disable no-console */
            console.error(
              `Codegen error for ${requestArgs.path} for ${lang}`,
              e.message,
            );
            // This error is sent to the front end which will send it to Sentry.
            codegenError = e.message;
          }
        }

        return {
          lang,
          generatedCode,
          codegenError,
          overriddenCodeBlock,
        };
      })
      .filter(Boolean);

    // TODO: Write this as an actual metric tagged by lang instead
    // of this hacky log. This is just to get some observability in splunk.
    const [seconds, nanoseconds] = process.hrtime(codegenStartTime);
    console.info(
      `CODEGEN-SNIPPET-TIMER path="${requestArgs.path}" time=${seconds * 1000 +
        nanoseconds / 1000000} generated=${
        generatedCodeBlocks.filter(
          (x) => !x.codegenError && !x.overriddenCodeBlock,
        ).length
      }`,
    );

    return new Tag(this.tag, {
      generatedCodeBlocks,
      codeTabGroupID: `codetab_${requestArgs.path.replace(/\//g, '.')}`,
      argsToHighlight: node.attributes.highlight,
    });
  },
};

export const CodegenSnippetOverride: MarkdocFragmentRegistration = {
  tag: 'codegen-snippet-override',
  attributes: {
    lang: {
      type: String,
      matches: LANGUAGES,
    },
  },
};
